import { createTimeline } from 'animejs/timeline';

// Keep the SVG's intrinsic coordinate system and final artwork intact. The
// animation only reveals those surfaces along their existing curved routes.
export function initHeroRibbons(artwork, data) {
    if (!artwork || !data?.routes || artwork.dataset.ribbonsInitialized) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches || document.hidden) return;

    artwork.dataset.ribbonsInitialized = 'true';
    // The traced SVG already contains every polygon. Read those exact coordinates
    // instead of downloading another copy solely for the reveal measurements.
    const pieces = Array.from(artwork.querySelectorAll('path[data-colour]'), (path) => {
        const polygon = Array.from(
            path.getAttribute('d').matchAll(/[ML](-?[\d.]+),(-?[\d.]+)/g),
            (match) => [Number(match[1]), Number(match[2])]
        );
        return {
            colour: path.dataset.colour,
            polygon,
            bounds: [Math.min(...polygon.map((point) => point[0]))]
        };
    });
    const duration = 6000;
    const state = { time: 0 };
    const ns = 'http://www.w3.org/2000/svg';
    const colours = ['purple', 'pink', 'orange', 'green', 'blue'];
    const clamp = (value) => Math.max(0, Math.min(1, value));
    const number = (value) => Math.round(value * 100) / 100;
    const point = (value) => value.map(number).join(',');

    function unitVector(from, to) {
        const dx = to[0] - from[0];
        const dy = to[1] - from[1];
        const length = Math.hypot(dx, dy) || 1;
        return [dx / length, dy / length];
    }

    function offsetPoint(origin, direction, distance) {
        return origin.map((value, axis) => value + direction[axis] * distance);
    }

    function curveThrough(points) {
        let d = '';
        for (let i = 0; i < points.length - 1; i += 1) {
            const previous = points[Math.max(0, i - 1)];
            const start = points[i];
            const end = points[i + 1];
            const next = points[Math.min(points.length - 1, i + 2)];
            const c1 = start.map((value, axis) => value + (end[axis] - previous[axis]) / 6);
            const c2 = end.map((value, axis) => value - (next[axis] - start[axis]) / 6);
            d += `C${point(c1)} ${point(c2)} ${point(end)}`;
        }
        return d;
    }

    // Routes follow the centres of the original visible surfaces. Smooth bridges
    // carry the reveal through the portions hidden behind the other ribbons.
    function makeRoute(sections) {
        const first = sections[0];
        const startDirection = unitVector(first[0], first[Math.min(4, first.length - 1)]);
        const start = offsetPoint(first[0], startDirection, -600);
        let d = `M${point(start)}L${point(first[0])}`;
        const measure = document.createElementNS(ns, 'path');
        const ranges = [];
        const measureLength = () => {
            measure.setAttribute('d', d);
            return measure.getTotalLength();
        };

        sections.forEach((points, index) => {
            if (index > 0) {
                const previous = sections[index - 1];
                const end = previous[previous.length - 1];
                const directionOut = unitVector(previous[Math.max(0, previous.length - 4)], end);
                const directionIn = unitVector(points[0], points[Math.min(3, points.length - 1)]);
                const handle = Math.min(
                    280,
                    Math.hypot(points[0][0] - end[0], points[0][1] - end[1]) / 3
                );
                const c1 = offsetPoint(end, directionOut, handle);
                const c2 = offsetPoint(points[0], directionIn, -handle);
                d += `C${point(c1)} ${point(c2)} ${point(points[0])}`;
            }
            const start = measureLength();
            d += curveThrough(points);
            ranges.push({ start, end: measureLength() });
        });

        const last = sections[sections.length - 1];
        const end = last[last.length - 1];
        const direction = unitVector(last[Math.max(0, last.length - 5)], end);
        d += `L${point(offsetPoint(end, direction, 650))}`;
        return { d, ranges };
    }

    // Match progress across the composition so a shorter route cannot run far
    // ahead of its neighbours. Monotone cubic interpolation keeps speed smooth
    // through each waypoint, including the long curved entrance and exit tails.
    function makeTiming(path, length) {
        const times = [0, 1500, 2350, 3100, 3850, 4500, 5700];
        const crossings = [700, 1400, 2100, 2800, 3400];
        const distances = [0];
        let distance = 0;
        crossings.forEach((x) => {
            while (distance < length && path.getPointAtLength(distance).x < x) distance += 12;
            distances.push(Math.min(distance, length));
        });
        distances.push(length);
        const slopes = times
            .slice(1)
            .map((time, i) => (distances[i + 1] - distances[i]) / (time - times[i]));
        const tangents = distances.map((_, i) => {
            if (i === 0) return slopes[0];
            if (i === distances.length - 1) return slopes[slopes.length - 1];
            const before = slopes[i - 1];
            const after = slopes[i];
            if (before <= 0 || after <= 0) return 0;
            const h1 = times[i] - times[i - 1];
            const h2 = times[i + 1] - times[i];
            const w1 = 2 * h2 + h1;
            const w2 = h2 + 2 * h1;
            return (w1 + w2) / (w1 / before + w2 / after);
        });
        return (time) => {
            if (time <= 0) return 0;
            if (time >= times[times.length - 1]) return length;
            const i = times.findIndex((value) => value > time) - 1;
            const span = times[i + 1] - times[i];
            const t = clamp((time - times[i]) / span);
            return (
                (2 * t ** 3 - 3 * t ** 2 + 1) * distances[i] +
                (t ** 3 - 2 * t ** 2 + t) * span * tangents[i] +
                (-2 * t ** 3 + 3 * t ** 2) * distances[i + 1] +
                (t ** 3 - t ** 2) * span * tangents[i + 1]
            );
        };
    }

    artwork.querySelector('#undersides')?.remove();
    const restingGroup = document.createElementNS(ns, 'g');
    restingGroup.id = 'resting-ribbons';
    restingGroup.setAttribute('opacity', '1');
    artwork.append(restingGroup);

    // Measure the real outline across the route normal. Ignore sections cut by
    // an overlapping ribbon; they inherit the nearest reliable full width.
    function makeWidthProfile(path, length, polygon) {
        let samples = [];
        const cross = (a, b) => a.x * b.y - a.y * b.x;
        for (let distance = 0; distance <= length; distance += 20) {
            const centre = path.getPointAtLength(distance);
            const before = path.getPointAtLength(Math.max(0, distance - 8));
            const after = path.getPointAtLength(Math.min(length, distance + 8));
            const span = Math.hypot(after.x - before.x, after.y - before.y) || 1;
            const normal = { x: -(after.y - before.y) / span, y: (after.x - before.x) / span };
            let left = Infinity;
            let right = Infinity;
            polygon.forEach((a, i) => {
                const b = polygon[(i + 1) % polygon.length];
                const edge = { x: b[0] - a[0], y: b[1] - a[1] };
                const delta = { x: a[0] - centre.x, y: a[1] - centre.y };
                const denominator = cross(normal, edge);
                if (Math.abs(denominator) < 0.0001) return;
                const t = cross(delta, edge) / denominator;
                const u = cross(delta, normal) / denominator;
                if (u < 0 || u > 1) return;
                if (t >= 0) right = Math.min(right, t);
                else left = Math.min(left, -t);
            });
            const width = left + right;
            if (width >= 170 && width <= 300 && Math.abs(left - right) < 65) {
                samples.push({ distance, width });
            }
        }
        if (!samples.length) return () => 208;
        // Exclude measurements narrowed by the pre-cut overlap edge.
        const orderedWidths = samples.map((sample) => sample.width).sort((a, b) => a - b);
        const minimumWidth = orderedWidths[Math.floor(orderedWidths.length / 2)] * 0.9;
        samples = samples.filter((sample) => sample.width >= minimumWidth);
        const widths = samples.map((sample, i) => {
            const neighbours = samples.slice(Math.max(0, i - 3), i + 4);
            return neighbours.reduce((sum, item) => sum + item.width, 0) / neighbours.length;
        });
        return (distance) => {
            const i = samples.findIndex((sample) => sample.distance >= distance);
            if (i === 0) return widths[0];
            if (i < 0) return widths[widths.length - 1];
            const t = clamp(
                (distance - samples[i - 1].distance) /
                    (samples[i].distance - samples[i - 1].distance)
            );
            return widths[i - 1] + (widths[i] - widths[i - 1]) * t;
        };
    }

    const strands = colours.map((colour, index) => {
        artwork.querySelector(`#clip-${colour}`)?.remove();
        const route = makeRoute(data.routes[colour]);
        const guide = document.createElementNS(ns, 'path');
        guide.setAttribute('d', route.d);
        const length = guide.getTotalLength();
        const distanceAt = makeTiming(guide, length);
        const colourPieces = pieces
            .map((piece, pieceIndex) => ({ ...piece, pieceIndex }))
            .filter((piece) => piece.colour === colour)
            .sort((a, b) => a.bounds[0] - b.bounds[0]);
        const group = document.createElementNS(ns, 'g');
        group.dataset.ribbon = colour;
        restingGroup.append(group);

        // Each visible surface has its own reveal. A brush crossing a hidden
        // bridge can no longer expose a nearby surface before reaching it.
        const surfaces = colourPieces.map((piece, sectionIndex) => {
            const points = data.routes[colour][sectionIndex];
            const first = points[0];
            const last = points[points.length - 1];
            const incoming = unitVector(first, points[1]);
            const outgoing = unitVector(points[points.length - 2], last);
            const extension = 400;
            const d =
                `M${point(offsetPoint(first, incoming, -extension))}L${point(first)}` +
                curveThrough(points) +
                `L${point(offsetPoint(last, outgoing, extension))}`;
            const mask = document.createElementNS(ns, 'mask');
            mask.id = `draw-${colour}-${sectionIndex}`;
            mask.setAttribute('maskUnits', 'userSpaceOnUse');
            mask.setAttribute('maskContentUnits', 'userSpaceOnUse');
            mask.setAttribute('x', '-1000');
            mask.setAttribute('y', '-1000');
            mask.setAttribute('width', String(data.width + 2000));
            mask.setAttribute('height', String(data.height + 2000));
            mask.style.maskType = 'alpha';
            artwork.querySelector('defs').append(mask);
            const makeBrush = (width) => {
                const path = document.createElementNS(ns, 'path');
                path.setAttribute('d', d);
                path.setAttribute('fill', 'none');
                path.setAttribute('stroke', 'white');
                path.setAttribute('stroke-width', width);
                path.setAttribute('stroke-linecap', 'round');
                path.setAttribute('stroke-linejoin', 'round');
                mask.append(path);
                return path;
            };
            const trail = makeBrush(360);
            // Fill the body up to the centre of the round head. A lagging brush
            // would narrow already-drawn artwork to the current tip width.
            trail.setAttribute('stroke-linecap', 'butt');
            const head = makeBrush(208);
            const localLength = head.getTotalLength();
            [trail, head].forEach((path) => {
                path.setAttribute('stroke-dasharray', `${localLength} ${localLength}`);
                path.setAttribute('stroke-dashoffset', localLength);
            });
            const surface = artwork.querySelector(`#piece-${piece.pieceIndex}`);
            surface.removeAttribute('clip-path');
            // Keep a group boundary so masking does not alter the surface fill.
            const layer = document.createElementNS(ns, 'g');
            layer.setAttribute('mask', `url(#${mask.id})`);
            layer.append(surface);
            group.append(layer);
            const start = route.ranges[sectionIndex].start - extension;
            return {
                pieceIndex: piece.pieceIndex,
                surface,
                rawDistanceAt: (time) =>
                    Math.max(0, Math.min(localLength, distanceAt(time - index * 35) - start)),
                sectionEnd: localLength - extension,
                head,
                trail,
                length: localLength,
                start: route.ranges[sectionIndex].start - extension,
                widthAt: makeWidthProfile(head, localLength, piece.polygon)
            };
        });
        return { index, surfaces, distanceAt };
    });

    // The PNG separates overlapping surfaces into cut shapes. Keep a rounded
    // tip inside its surface until the crossing ribbon actually covers that cut.
    // Dependencies follow the weave from left to right and are resolved once.
    const overlaps = {
        0: 4,
        1: 5,
        4: 6,
        5: 7,
        6: 11,
        7: 12,
        8: 4,
        10: 8,
        11: 16,
        12: 15,
        14: 2,
        16: 1,
        17: 4,
        18: 4
    };
    const surfaces = new Map(
        strands.flatMap((strand) => strand.surfaces).map((surface) => [surface.pieceIndex, surface])
    );

    function findSafeEnd(surface) {
        for (
            let distance = surface.sectionEnd;
            distance > Math.max(420, surface.sectionEnd - 400);
            distance -= 8
        ) {
            const centre = surface.head.getPointAtLength(distance);
            const before = surface.head.getPointAtLength(distance - 6);
            const after = surface.head.getPointAtLength(distance + 6);
            const direction = Math.atan2(after.y - before.y, after.x - before.x);
            const radius = surface.widthAt(distance) * 0.48;
            const fits = [-80, -55, -30, 0, 30, 55, 80].every((degrees) => {
                const angle = direction + (degrees * Math.PI) / 180;
                return surface.surface.isPointInFill(
                    new DOMPoint(
                        centre.x + Math.cos(angle) * radius,
                        centre.y + Math.sin(angle) * radius
                    )
                );
            });
            if (fits) return distance;
        }
        return Math.max(400, surface.sectionEnd - 280);
    }

    function resolveHandoff(surface) {
        if (surface.distanceAt) return;
        const target = surfaces.get(overlaps[surface.pieceIndex]);
        if (!target) {
            surface.distanceAt = surface.rawDistanceAt;
            return;
        }
        resolveHandoff(target);
        const safeEnd = findSafeEnd(surface);
        const crossing = surface.head.getPointAtLength(surface.sectionEnd);
        let closest = Infinity;
        let crossingDistance = 400;
        for (let distance = 400; distance <= target.sectionEnd; distance += 8) {
            const candidate = target.head.getPointAtLength(distance);
            const separation = Math.hypot(candidate.x - crossing.x, candidate.y - crossing.y);
            if (separation < closest) {
                closest = separation;
                crossingDistance = distance;
            }
        }
        // Release once the covering tip has advanced into the overlap.
        const coveredAt = Math.min(
            target.sectionEnd,
            crossingDistance + target.widthAt(crossingDistance) * 0.25
        );
        let low = 0;
        let high = duration - 250;
        for (let i = 0; i < 18; i += 1) {
            const time = (low + high) / 2;
            if (target.distanceAt(time) < coveredAt) low = time;
            else high = time;
        }
        const release = high;
        surface.distanceAt = (time) => {
            const raw = surface.rawDistanceAt(time);
            const t = clamp((time - release) / 220);
            const ease = t * t * (3 - 2 * t);
            const approach = 80;
            const held =
                raw <= safeEnd - approach
                    ? raw
                    : safeEnd - approach * Math.exp(-(raw - safeEnd + approach) / approach);
            return held + (raw - held) * ease;
        };
    }
    surfaces.forEach(resolveHandoff);

    function render() {
        const time = state.time;
        strands.forEach((strand) => {
            strand.surfaces.forEach((surface) => {
                const localDistance = surface.distanceAt(time);
                surface.head.setAttribute('stroke-width', surface.widthAt(localDistance));
                surface.head.setAttribute('stroke-dashoffset', surface.length - localDistance);
                surface.trail.setAttribute('stroke-dashoffset', surface.length - localDistance);
            });
        });
    }

    function stopListening() {
        reducedMotion.removeEventListener('change', onMotionChange);
        document.removeEventListener('visibilitychange', onVisibilityChange);
    }

    function finish() {
        timeline.pause();
        state.time = duration;
        render();
        stopListening();
    }

    function onMotionChange() {
        if (reducedMotion.matches) finish();
    }

    function onVisibilityChange() {
        if (document.hidden) finish();
    }

    const timeline = createTimeline({
        autoplay: false,
        onRender: render,
        onComplete: () => {
            render();
            stopListening();
        }
    });
    timeline.add(state, { time: [0, duration], duration, ease: 'linear' }, 0);
    // Retain the preview's timing relationships at its approved 4× playback speed.
    timeline.speed = 4;

    reducedMotion.addEventListener('change', onMotionChange);
    document.addEventListener('visibilitychange', onVisibilityChange);

    render();
    timeline.play();
    return timeline;
}
